import intl from 'react-intl-universal';
import moment from 'moment';
import {
  ProjectDrillDownItem,
  RequestsDrillDownItem,
  TaskDrillDownItem,
  UserDrillDownItem,
  CapacityManagementDrillDownItem,
  EventDrillDownItem,
} from 'types/store/normalized/insightsDrillDown';
import {
  CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS,
  PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS,
  REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS,
  TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS,
  USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS,
} from 'utils/constants/dashboard';
import { DATE } from 'utils/constants';
import { capitalize } from 'lodash';
import { formatDate } from 'utils/formatters';

export const handleRequestExportData = (
  tableItems: RequestsDrillDownItem[]
) => {
  const data = tableItems.map((row) => row);
  const csvArray = [
    [
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_NUMBER,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_TITLE,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STATUS,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUESTER_NAME,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.REQUEST_OWNER,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECTS,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINESS_UNIT,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.INTERNAL_PRIORITY,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.FORM_USED,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CREATION_DATE,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SUBMISSION_DATE,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DECISION_DATE,
      REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LAST_UPDATE,
    ],
    ...data.map((row) => [
      row.request_number,
      row.title,
      intl.get(
        `REQUESTS_LIST_PAGE.TABLE.STATUS_LABEL.${row.request_status.toUpperCase()}`
      ),
      row.request_type,

      `${row.requester?.first_name ?? ''} ${row.requester?.last_name ?? ''}`,

      '"' +
        (row.owners?.length > 0
          ? row.owners.map((i) => `${i.first_name ?? ''} ${i.last_name ?? ''}`)
          : []
        ).join(', ') +
        '"',

      '"' +
        (row.projects && row.projects?.length > 0
          ? row.projects.map((i) => i.title ?? '')
          : []
        ).join(', ') +
        '"',

      row.business_unit_title,
      capitalize(row.ld_priority ?? ''),
      row.form_title,
      row.creation_date,
      row.submission_date,
      row.decision_date,
      row.last_update_date,
    ]),
  ];
  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_223_${new Date().toISOString()}.csv`;
  link.click();
};

export const handleProjectExportData = (tableItems: ProjectDrillDownItem[]) => {
  const data = tableItems.map((row) => row);
  const csvArray = [
    [
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_NUMBER,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TITLE,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_STATUS,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_PRIORITY,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_OWNER,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_HEALTH,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINEES_UNITS,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TOTAL_DEMAND_HOURS,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CATEGORY_NAME,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.START_DATE,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TARGET_COMPLETION_DATE,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.COMPLETION_DATE,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.RESOURCING_TYPE,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROCESS_NAME,
      PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STAGE_NAME,
    ],
    ...data.map((row) => [
      row.project_number,
      row.title,
      row.project_status,
      capitalize(row.project_priority ?? ''),
      row.owners?.length
        ? `"${row.owners
            .map((i) => `${i.first_name ?? ''} ${i.last_name ?? ''}`)
            .join(', ')}"`
        : '""',
      row.project_health,
      row.business_units?.length
        ? `"${row.business_units.map((i) => i.title ?? 'N/A').join(', ')}"`
        : '""',
      row.total_demand_hours,
      row.category_name,
      row.programs?.length
        ? `"${row.programs.map((i) => i.title ?? 'N/A').join(', ')}"`
        : '""',
      row.start_date,
      row.target_completion_date,
      row.completion_date,
      row.resourcing_type,
      row.process_name,
      row.stage_name,
    ]),
  ];

  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_project_224_${new Date().toISOString()}.csv`;
  link.click();
};

export const handleTaskExportData = (tableItems: TaskDrillDownItem[]) => {
  const data = tableItems.map((row) => row);
  const csvArray = [
    [
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NUMBER,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NAME,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.STATUS,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ASSIGNEES,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.START_DATE,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DUE_DATE,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DEMAND_EST_HOURS,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS,
      TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS,
    ],
    ...data.map((row) => [
      row.task_number,
      row.task_name,
      row.task_status,
      row.task_type,
      row.project_title,
      row.programs?.length
        ? `"${row.programs.map((i) => i.title).join(', ')}"`
        : '""',
      row.assignee_data?.length
        ? `"${row.assignee_data
            .map((i) => `${i.first_name ?? ''} ${i.last_name ?? ''}`)
            .join(', ')}"`
        : '""',
      row.start_date,
      row.due_date,
      row.demand_hours_in_range,
      row.allocated_hours_in_range,
      row.actual_hours_in_range,
    ]),
  ];
  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_task_225_${new Date().toISOString()}.csv`;
  link.click();
};

export const handleUserExportData = (tableItems: UserDrillDownItem[]) => {
  const data = tableItems.map((row) => row);
  const csvArray = [
    [
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.NAME,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TIME_OFF,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DEFAULT_CAPACITY_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CAPACITY_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.AVAILABLE_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SCHEDULED_HOURS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.JOB_TITLE,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.JOB_FUNCTIONS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.SKILLS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LEARNING_TEAMS,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TYPE,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ROLE,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.EMAIL,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.CREATED_AT,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.LAST_LOGIN_DATE,
      USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS.DISABLED_AT,
    ],
    ...data.map((row) => [
      row.user_name,
      row.timeoff_hours_in_range,
      row.default_capacity_hours_in_range,
      row.capacity_hours_in_range,
      row.allocated_hours_in_range,
      row.actual_hours_in_range,
      row.available_hours_in_range,
      row.scheduled_hours_in_range,

      row.job_title,
      '"' + (row.job_functions?.join(', ') || '') + '"',
      '"' + (row.skills?.join(', ') || '') + '"',
      '"' +
        (row.learning_teams?.map((team) => team.name).join(', ') || '') +
        '"',
      row.user_type,
      row.user_role,
      row.email,
      '"' + formatDate(row.created_at) + '"',
      '"' + (row.last_login_date ? formatDate(row.last_login_date) : '') + '"',
      row.user_data.disabled_at
        ? '"' + formatDate(row.user_data.disabled_at) + '"'
        : '',
    ]),
  ];
  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_users_${new Date().toISOString()}.csv`;
  link.click();
};

export const handleCapacityManagementExportData = (
  tableItems: CapacityManagementDrillDownItem[]
) => {
  const data = tableItems.map((row) => row);
  const csvArray = [
    [
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.NAME,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_TYPE,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NUMBER,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_TITLE,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_STATUS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_TYPE,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_TITLE,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINESS_UNITS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_TEAMS,
      CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_TEAMS,
    ],
    ...data.map((row) => [
      row.user_name,
      row.user_type,
      row.task_number,
      row.task_name,
      row.task_status,
      row.task_type,
      row.allocated_hours_in_range,
      row.actual_hours_in_range,
      row.project_title,
      row.business_units?.length
        ? `"${row.business_units.map((i) => i.title ?? 'N/A').join(', ')}"`
        : '""',
      row.programs?.length
        ? `"${row.programs.map((i) => i.title ?? 'N/A').join(', ')}"`
        : '""',
      row.project_learning_teams?.length
        ? `"${row.project_learning_teams
            .map((i) => i.name ?? 'N/A')
            .join(', ')}"`
        : '""',
      row.user_learning_teams?.length
        ? `"${row.user_learning_teams.map((i) => i.name ?? 'N/A').join(', ')}"`
        : '""',
    ]),
  ];
  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_capacity_management_${new Date().toISOString()}.csv`;
  link.click();
};

export const handleEventExportData = (
  tableItems: Array<EventDrillDownItem>
): void => {
  const csvArray = [
    [
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.EVENT_TITLE'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.EVENT_TYPE'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.START'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.END'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.FACILITATORS'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.RECURRING_EVENT'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.NUMBER_OF_DAYS'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.DAILY_DURATION'),
      intl.get('DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.TOTAL_DURATION'),
      intl.get(
        'DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.NUMBER_OF_DAYS_IN_RANGE'
      ),
      intl.get(
        'DASHBOARDS_EVENT_DRILLDOWN_PAGE.TABLE.HEAD.TOTAL_DURATION_IN_RANGE'
      ),
    ],
    ...tableItems.map((event) => [
      '"' + event.title + '"',
      '"' + event.event_type + '"',
      '"' +
        (event.is_all_day
          ? moment(event.start_timestamp).format(DATE.SHORT_FORMAT)
          : moment(event.start_timestamp).format(DATE.SHORT_FORMAT_WITH_TIME)) +
        '"',
      '"' +
        (event.is_all_day
          ? moment(event.end_timestamp).format(DATE.SHORT_FORMAT)
          : moment(event.end_timestamp).format(DATE.SHORT_FORMAT_WITH_TIME)) +
        '"',
      '"' +
        (event.facilitator_count > 0
          ? event.facilitator_data
              .map(
                (facilitator) =>
                  `${facilitator.first_name} ${facilitator.last_name}`
              )
              .join(', ')
          : '') +
        '"',
      '"' + (event.is_recurring ? intl.get('YES') : intl.get('NO')) + '"',
      '"' + event.event_days + '"',
      '"' +
        intl.get('HOURS_AMOUNT', {
          hours: event.duration_hours_per_day,
        }) +
        '"',
      '"' +
        intl.get('HOURS_AMOUNT', {
          hours: event.total_duration_hours,
        }) +
        '"',
      '"' + event.event_days_in_range + '"',
      '"' +
        intl.get('HOURS_AMOUNT', {
          hours: event.duration_hours_in_range,
        }) +
        '"',
    ]),
  ];
  const csvContent = csvArray.map((e) => e.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `drilldown_export_events_${new Date().toISOString()}.csv`;
  link.click();
};
